import type { ApplicationConfig } from '@angular/core';
import { inject, provideAppInitializer } from '@angular/core';
import { appRoutes } from './app.routes';
import {
  ALLOW_BRANDING_QUERY_PARAM,
  BRAND_REGISTRY,
  SPARTE_API_BASE_PATH,
  infrastructureProviders,
} from '@comparit/infrastructure';
import { provideAppRouter, uiProvidersConfig } from '@comparit/core';
import { kfzBrandRegistry } from './brands/brand-registry';
import { environment } from '../environments/environment';
import { ENABLE_TEST_DATA_VIEWER } from '../constants/enable-test-data-viewer.token';
import { BASE_PATH } from '../interfaces/generated';
import { BASE_PATH as COMMON_API_BASE_PATH } from '@comparit/common-api';
import { kundeReauthInterceptor } from '../interceptors/kunde-reauth.interceptor';
import { KundeLogoutPromptService } from '../services/kunde-logout-prompt.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppRouter(appRoutes),
    // The libraries decide the API root (infrastructureProviders, from the brand or environment);
    // the generated clients follow it. A lib may never name an app's generated BASE_PATH itself.
    // Two tokens, two clients: this app's own, and the shared one that libs/shared's AddressService
    // calls. Distinct InjectionTokens — providing one does not provide the other, and a missing
    // second provider compiles cleanly and only fails when address validation first runs.
    { provide: BASE_PATH, useExisting: SPARTE_API_BASE_PATH },
    { provide: COMMON_API_BASE_PATH, useExisting: SPARTE_API_BASE_PATH },
    {
      provide: ENABLE_TEST_DATA_VIEWER,
      useValue: environment.enableTestDataViewer,
    },
    {
      provide: ALLOW_BRANDING_QUERY_PARAM,
      useValue: environment.allowBrandingQueryParam,
    },
    {
      provide: BRAND_REGISTRY,
      useValue: kfzBrandRegistry,
    },
    // Reopens the brand's login dialog on a 401 and retries (KFZ-2512). Passed
    // in rather than registered in the lib: reacting to a rejected session is
    // app policy, and it has to wrap kundeTokenInterceptor for the retry to
    // carry the new token.
    ...infrastructureProviders({
      apiUrl: environment.apiUrl,
      interceptors: [kundeReauthInterceptor],
    }),
    provideAppInitializer(() => inject(KundeLogoutPromptService).watch()),
    ...uiProvidersConfig,
  ],
};
